// Single entry point callers use to capture and sync intake turns.
// Wraps IndexedDBStore (durable local writes) + RetryQueue (eventual
// Supabase sync) behind one API. The Clerk session token is injected
// via setAuthToken() so this module never reaches into Clerk hooks
// directly — keeps it usable from test pages, hooks, or services
// alike.

import IndexedDBStore from './IndexedDBStore.js';
import RetryQueue from './RetryQueue.js';

const SAVE_ENDPOINT = '/api/save-intake-message';
const GET_ENDPOINT = '/api/get-intake-messages';

export default class IntakeRepository {
  constructor({ telemetry } = {}) {
    this._telemetry = telemetry || null;
    this._idb = new IndexedDBStore();
    this._authToken = null;

    const apiClient = {
      syncMessage: (record) => this._postMessage(record),
    };
    this._queue = new RetryQueue({
      idbStore: this._idb,
      apiClient,
      telemetry: this._telemetry,
    });

    this._listeners = new Map();
    this._initialized = false;

    this._queue.on('syncSuccess', ({ messageId }) =>
      this._emit('turnSynced', { messageId })
    );
    this._queue.on('syncFailure', (payload) =>
      this._emit('syncFailure', payload)
    );
    this._queue.on('syncGiveUp', (payload) =>
      this._emit('syncGiveUp', payload)
    );
  }

  async init() {
    if (this._initialized) return;
    await this._idb.init();
    this._queue.start();
    this._initialized = true;
  }

  setAuthToken(token) {
    this._authToken = token || null;
  }

  startSession() {
    return generateUuid();
  }

  async resumeSession(sessionId) {
    if (this._authToken) {
      try {
        const res = await fetch(
          `${GET_ENDPOINT}?session_id=${encodeURIComponent(sessionId)}`,
          { headers: { Authorization: `Bearer ${this._authToken}` } }
        );
        if (res.ok) {
          const data = await res.json();
          return data.messages || [];
        }
      } catch {
        // Fall through to IDB
      }
    }
    return this._idb.getMessagesBySession(sessionId);
  }

  async saveTurn({ session_id, role, content, metadata }) {
    if (!this._initialized) {
      throw new Error('IntakeRepository.init() must be called first');
    }
    if (!session_id) throw new Error('session_id required');
    if (role !== 'guide' && role !== 'user') {
      throw new Error("role must be 'guide' or 'user'");
    }
    if (typeof content !== 'string' || !content.length) {
      throw new Error('content required');
    }

    const existing = await this._idb.getMessagesBySession(session_id);
    const turn_number =
      existing.reduce((max, m) => Math.max(max, m.turn_number || 0), 0) + 1;

    const record = {
      id: generateUuid(),
      session_id,
      turn_number,
      role,
      content,
      metadata: metadata || {},
      created_at: new Date().toISOString(),
      sync_status: 'pending',
      retry_count: 0,
      last_retry_at: null,
      failure_reason: null,
    };

    await this._idb.putMessage(record);
    this._emit('turnPersisted', { record });
    this._telemetry?.track('intake.turn.persisted', {
      messageId: record.id,
      sessionId: session_id,
      turnNumber: turn_number,
      role,
    });

    this._queue.enqueue(record.id).catch(() => {});
    return record;
  }

  async getMessages(sessionId) {
    if (!this._initialized) {
      throw new Error('IntakeRepository.init() must be called first');
    }
    return this._idb.getMessagesBySession(sessionId);
  }

  async completeSession(sessionId) {
    await this._queue.processNow();
    this._telemetry?.track('intake.session.completed', { sessionId });
  }

  getRetryQueue() {
    return this._queue;
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) set.delete(callback);
  }

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[IntakeRepository] listener threw:', e);
      }
    }
  }

  async _postMessage(record) {
    if (!this._authToken) {
      throw new Error('no auth token — call setAuthToken() before syncing');
    }
    const res = await fetch(SAVE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._authToken}`,
      },
      body: JSON.stringify({
        session_id: record.session_id,
        turn_number: record.turn_number,
        role: record.role,
        content: record.content,
        metadata: record.metadata || {},
      }),
    });
    if (!res.ok) {
      const snippet = await res
        .text()
        .then((t) => t.slice(0, 200))
        .catch(() => '');
      throw new Error(`save-intake-message ${res.status}: ${snippet}`);
    }
  }
}

function generateUuid() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older environments — RFC4122 v4-ish, good enough for
  // a session id. Modern browsers all support crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
